<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import CustomAttribute from 'dashboard/components/CustomAttribute.vue';
import ContactAPI from 'dashboard/api/contacts';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CreateEmpresaModal from './CreateEmpresaModal.vue';
import EmpresaContactRolesCell from './EmpresaContactRolesCell.vue';
import EmpresaSocialLinks from './EmpresaSocialLinks.vue';
import EmpresaNotes from './EmpresaNotes.vue';
import EmpresaDocuments from './EmpresaDocuments.vue';
import EmpresaHistorial from './EmpresaHistorial.vue';
import CreateProjectModal from '../projects/CreateProjectModal.vue';
import ProjectsAPI from 'dashboard/api/projects';

const props = defineProps({
  empresaId: {
    type: [String, Number],
    required: true,
  },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const editModalRef = ref(null);
const projectModalRef = ref(null);
const activeTab = ref('contacts');
const sidebarTab = ref('atributos');

// Projects belonging to this empresa (loaded lazily when the Projects tab opens).
const empresaProjects = ref([]);
const isLoadingProjects = ref(false);

const sidebarTabs = computed(() => [
  { key: 'atributos', label: t('EMPRESAS.SIDEBAR.ATRIBUTOS') },
  { key: 'historial', label: t('EMPRESAS.SIDEBAR.HISTORIAL') },
  { key: 'notas', label: t('EMPRESAS.SIDEBAR.NOTAS') },
]);

const empresa = computed(() => store.getters.getEmpresaById(props.empresaId));
const contacts = computed(() => empresa.value?.contacts || []);
const socialProfiles = computed(
  () => empresa.value?.additional_attributes?.social_profiles || {}
);

const tabs = computed(() => [
  {
    key: 'contacts',
    label: t('EMPRESAS.TABS.CONTACTS'),
    icon: 'i-lucide-users',
  },
  {
    key: 'documents',
    label: t('EMPRESAS.TABS.DOCUMENTS'),
    icon: 'i-lucide-file-text',
  },
  {
    key: 'projects',
    label: t('EMPRESAS.TABS.PROJECTS'),
    icon: 'i-lucide-folder-kanban',
  },
]);

const empresaAttributeDefinitions = computed(() =>
  store.getters['attributes/getAttributesByModel']('empresa_attribute')
);

// Custom field definitions excluding the role-vocabulary attribute (contact_role).
const empresaCustomFieldDefinitions = computed(() =>
  (empresaAttributeDefinitions.value || []).filter(
    d => d.attribute_key !== 'contact_role'
  )
);

// Role vocabulary from the 'contact_role' empresa list custom-attribute.
const roleVocabulary = computed(() => {
  const defs = empresaAttributeDefinitions.value || [];
  const roleDef = defs.find(d => d.attribute_key === 'contact_role');
  return roleDef?.attribute_values || [];
});

const search = ref('');
const results = ref([]);
const showCreate = ref(false);
const newContact = ref({ name: '', email: '', phone_number: '' });

const empresaCustomAttributes = computed(
  () => empresa.value?.custom_attributes || {}
);

const websiteHref = computed(() => {
  const url = empresa.value?.website;
  if (!url) return null;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
});

const loadEmpresa = () =>
  store.dispatch('fetchEmpresa', { id: props.empresaId });

const loadEmpresaProjects = async () => {
  isLoadingProjects.value = true;
  try {
    // Read directly via the API so we don't overwrite the shared global
    // projects store (this is a scoped, read-only view for this empresa).
    const { data } = await ProjectsAPI.get({
      empresa_id: Number(props.empresaId),
    });
    empresaProjects.value = data?.payload || [];
  } catch (error) {
    empresaProjects.value = [];
  } finally {
    isLoadingProjects.value = false;
  }
};

const openProject = project =>
  router.push({ name: 'project_details', params: { projectId: project.id } });

const onProjectCreated = () => loadEmpresaProjects();

const projectStatusClass = status =>
  ({
    draft: 'bg-n-alpha-1 text-n-slate-11',
    active: 'bg-n-teal-3 text-n-teal-11',
    on_hold: 'bg-n-amber-3 text-n-amber-11',
    completed: 'bg-n-blue-3 text-n-blue-11',
    cancelled: 'bg-n-ruby-3 text-n-ruby-11',
  })[status] || 'bg-n-alpha-1 text-n-slate-11';

const onAttributeUpdate = async (key, value) => {
  const updated = { ...empresaCustomAttributes.value, [key]: value };
  await store.dispatch('updateEmpresa', {
    id: props.empresaId,
    custom_attributes: updated,
  });
  loadEmpresa();
};

const deleteEmpresa = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('EMPRESAS.DETAILS.DELETE_CONFIRM'))) return;
  await store.dispatch('deleteEmpresa', props.empresaId);
  router.push({ name: 'empresas_list' });
};

const removeContact = async contact => {
  await store.dispatch('contacts/update', { id: contact.id, empresa_id: null });
  loadEmpresa();
};

const openContact = contact =>
  router.push({ name: 'contacts_edit', params: { contactId: contact.id } });

const runSearch = async () => {
  if (!search.value.trim()) {
    results.value = [];
    return;
  }
  const { data } = await ContactAPI.search(search.value.trim(), 1);
  results.value = (data?.payload || []).filter(
    c => c.empresa_id !== Number(props.empresaId)
  );
};

let searchTimer = null;
const debouncedSearch = () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(runSearch, 300);
};

const attach = async contact => {
  // eslint-disable-next-line no-alert
  if (
    contact.empresa_id &&
    !window.confirm(t('EMPRESAS.DETAILS.REASSIGN_CONFIRM'))
  ) {
    return;
  }
  await store.dispatch('contacts/update', {
    id: contact.id,
    empresa_id: Number(props.empresaId),
  });
  search.value = '';
  results.value = [];
  loadEmpresa();
};

const createAndAttach = async () => {
  if (!newContact.value.name.trim()) return;
  try {
    await store.dispatch('contacts/create', {
      ...newContact.value,
      empresa_id: Number(props.empresaId),
    });
    newContact.value = { name: '', email: '', phone_number: '' };
    showCreate.value = false;
    loadEmpresa();
  } catch (error) {
    // leave the form open so the user can correct and retry
  }
};

const updateRoles = async (contact, roles) => {
  await store.dispatch('contacts/update', {
    id: contact.id,
    empresa_roles: roles,
  });
  loadEmpresa();
};

const setUpRoles = async () => {
  if (roleVocabulary.value.length) return;
  try {
    await store.dispatch('attributes/create', {
      attribute_display_name: 'Contact role',
      attribute_description: 'Role of a contact within an empresa',
      attribute_model: 4,
      attribute_display_type: 6,
      attribute_key: 'contact_role',
      attribute_values: [
        'Billing',
        'Technical',
        'Commercial',
        'Administrative',
        'General',
      ],
      regex_pattern: null,
      regex_cue: null,
    });
    await store.dispatch('attributes/get');
  } catch (error) {
    // ignore (e.g. already created)
  }
};

watch(activeTab, tab => {
  if (tab === 'projects' && !empresaProjects.value.length)
    loadEmpresaProjects();
});

onMounted(() => {
  loadEmpresa();
  store.dispatch('attributes/get');
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <div v-if="empresa" class="flex-1 min-h-0 overflow-y-auto">
      <div class="flex flex-col w-full gap-6 px-6 py-6 lg:px-8">
        <!-- Back -->
        <Button
          variant="link"
          color="slate"
          size="sm"
          icon="i-lucide-arrow-left"
          :label="t('EMPRESAS.ACTIONS.BACK')"
          class="self-start"
          @click="router.push({ name: 'empresas_list' })"
        />

        <!-- Header -->
        <header class="flex items-start justify-between gap-4">
          <div class="flex items-center min-w-0 gap-3">
            <Avatar :name="empresa.name" :size="48" />
            <div class="flex flex-col min-w-0">
              <h1 class="text-xl font-semibold truncate text-n-slate-12">
                {{ empresa.name }}
              </h1>
              <div class="flex items-center gap-3">
                <p class="text-sm text-n-slate-10">
                  {{ empresa.contacts_count }}
                  {{ t('EMPRESAS.FIELDS.CONTACTS_COUNT') }}
                </p>
                <EmpresaSocialLinks :profiles="socialProfiles" />
              </div>
            </div>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <Button
              variant="faded"
              color="slate"
              size="sm"
              icon="i-lucide-pencil"
              :label="t('EMPRESAS.ACTIONS.EDIT')"
              @click="editModalRef?.open()"
            />
            <Button
              variant="ghost"
              color="ruby"
              size="sm"
              icon="i-lucide-trash-2"
              :label="t('EMPRESAS.ACTIONS.DELETE')"
              @click="deleteEmpresa"
            />
          </div>
        </header>

        <!-- Body: company info (priority) + tabs, with right sidebar -->
        <div class="flex flex-col gap-6 lg:flex-row lg:items-start">
          <!-- Main -->
          <div class="flex flex-col flex-1 min-w-0 gap-6">
            <!-- Company info (priority, full width) -->
            <section
              class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-2 sm:p-5"
            >
              <h2
                class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
              >
                <span class="i-lucide-building-2" />
                {{ t('EMPRESAS.DETAILS.INFO_TITLE') }}
              </h2>
              <div
                class="grid grid-cols-1 gap-x-8 gap-y-4 text-sm sm:grid-cols-3"
              >
                <div class="flex flex-col gap-0.5 min-w-0">
                  <span class="text-xs text-n-slate-10">
                    {{ t('EMPRESAS.FIELDS.EMAIL_DOMAIN') }}
                  </span>
                  <span class="truncate text-n-slate-12">
                    {{ empresa.email_domain || '—' }}
                  </span>
                </div>
                <div class="flex flex-col gap-0.5 min-w-0">
                  <span class="text-xs text-n-slate-10">
                    {{ t('EMPRESAS.FIELDS.WEBSITE') }}
                  </span>
                  <a
                    v-if="websiteHref"
                    :href="websiteHref"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center gap-1 truncate text-n-blue-text hover:underline"
                  >
                    {{ empresa.website }}
                    <span class="i-lucide-external-link size-3.5 shrink-0" />
                  </a>
                  <span v-else class="text-n-slate-12">—</span>
                </div>
                <div class="flex flex-col gap-0.5 min-w-0">
                  <span class="text-xs text-n-slate-10">
                    {{ t('EMPRESAS.FIELDS.PHONE_NUMBER') }}
                  </span>
                  <span class="text-n-slate-12">
                    {{ empresa.phone_number || '—' }}
                  </span>
                </div>
              </div>
            </section>

            <!-- Tabbed content -->
            <section
              class="flex flex-col overflow-hidden border rounded-xl border-n-weak bg-n-solid-2"
            >
              <nav class="flex items-center gap-1 px-2 border-b border-n-weak">
                <button
                  v-for="tab in tabs"
                  :key="tab.key"
                  type="button"
                  class="flex items-center gap-1.5 px-3 h-10 -mb-px text-sm border-b-2 transition-colors"
                  :class="
                    activeTab === tab.key
                      ? 'border-n-brand text-n-slate-12 font-medium'
                      : 'border-transparent text-n-slate-10 hover:text-n-slate-12'
                  "
                  @click="activeTab = tab.key"
                >
                  <span :class="tab.icon" class="size-4" />
                  {{ tab.label }}
                </button>
              </nav>

              <div class="p-4 sm:p-5">
                <!-- Contacts tab -->
                <div
                  v-show="activeTab === 'contacts'"
                  class="flex flex-col gap-3"
                >
                  <!-- Search + create toggle -->
                  <div class="flex flex-col gap-2">
                    <div class="flex items-center gap-2">
                      <div class="relative flex-1 min-w-0">
                        <span
                          class="absolute -translate-y-1/2 i-lucide-search left-2.5 top-1/2 text-n-slate-10 size-4"
                        />
                        <input
                          v-model="search"
                          type="text"
                          class="w-full h-8 pl-8 pr-3 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
                          :placeholder="t('EMPRESAS.DETAILS.SEARCH_CONTACTS')"
                          @input="debouncedSearch"
                          @keyup.enter="runSearch"
                        />
                      </div>
                      <Button
                        variant="faded"
                        color="slate"
                        size="sm"
                        icon="i-lucide-user-plus"
                        :label="t('EMPRESAS.DETAILS.CREATE_CONTACT')"
                        class="shrink-0"
                        @click="showCreate = !showCreate"
                      />
                    </div>

                    <!-- Search results -->
                    <ul
                      v-if="results.length"
                      class="overflow-hidden border divide-y rounded-md border-n-weak divide-n-weak bg-n-solid-2"
                    >
                      <li
                        v-for="result in results"
                        :key="result.id"
                        class="flex items-center justify-between gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-n-alpha-1"
                        @click="attach(result)"
                      >
                        <span class="flex items-center min-w-0 gap-2">
                          <Avatar
                            :name="result.name"
                            :src="result.thumbnail"
                            :size="22"
                          />
                          <span class="font-medium truncate text-n-slate-12">
                            {{ result.name }}
                          </span>
                          <span class="truncate text-n-slate-10">
                            {{ result.email || '' }}
                          </span>
                        </span>
                        <span
                          v-if="result.empresa_id"
                          class="text-xs shrink-0 text-n-amber-11"
                        >
                          {{ t('EMPRESAS.DETAILS.ALREADY_LINKED') }}
                        </span>
                      </li>
                    </ul>

                    <!-- Inline create contact -->
                    <div
                      v-if="showCreate"
                      class="flex flex-col gap-2 p-3 border rounded-xl border-n-weak bg-n-solid-2"
                    >
                      <input
                        v-model="newContact.name"
                        class="h-8 px-2.5 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
                        :placeholder="t('EMPRESAS.FIELDS.NAME')"
                      />
                      <input
                        v-model="newContact.email"
                        class="h-8 px-2.5 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
                        :placeholder="t('EMPRESAS.DETAILS.CONTACT_EMAIL')"
                      />
                      <input
                        v-model="newContact.phone_number"
                        class="h-8 px-2.5 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
                        :placeholder="t('EMPRESAS.FIELDS.PHONE_NUMBER')"
                      />
                      <Button
                        :disabled="!newContact.name.trim()"
                        size="sm"
                        color="blue"
                        :label="t('EMPRESAS.DETAILS.ADD_CONTACT')"
                        class="self-start"
                        @click="createAndAttach"
                      />
                    </div>
                  </div>

                  <!-- Contacts table (Notion/ClickUp-style, compact) -->
                  <p
                    v-if="!contacts.length"
                    class="py-2 text-sm text-n-slate-10"
                  >
                    {{ t('EMPRESAS.DETAILS.CONTACTS_EMPTY') }}
                  </p>
                  <div v-else class="overflow-x-auto">
                    <table class="w-full text-sm">
                      <thead>
                        <tr
                          class="text-xs font-normal text-n-slate-10 [&>th]:font-normal [&>th]:text-left [&>th]:py-1.5 [&>th]:pr-3"
                        >
                          <th>{{ t('EMPRESAS.DETAILS.COL_CONTACT') }}</th>
                          <th>{{ t('EMPRESAS.DETAILS.ROLES_LABEL') }}</th>
                          <th class="w-8" />
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="contact in contacts"
                          :key="contact.id"
                          class="group transition-colors [&>td]:border-t [&>td]:border-n-weak/60 [&>td]:py-1.5 [&>td]:pr-3 [&>td]:align-middle hover:bg-n-alpha-1"
                        >
                          <!-- Contact (click to open details) -->
                          <td class="min-w-0">
                            <button
                              v-tooltip.top="t('EMPRESAS.DETAILS.OPEN_CONTACT')"
                              type="button"
                              class="flex items-center min-w-0 gap-2 group/name"
                              @click="openContact(contact)"
                            >
                              <Avatar
                                :name="contact.name"
                                :src="contact.thumbnail"
                                :size="24"
                              />
                              <span
                                class="font-medium truncate text-n-slate-12 group-hover/name:text-n-blue-text group-hover/name:underline"
                              >
                                {{ contact.name }}
                              </span>
                            </button>
                          </td>
                          <!-- Roles (multi-select) -->
                          <td>
                            <EmpresaContactRolesCell
                              v-if="roleVocabulary.length"
                              :model-value="contact.empresa_roles || []"
                              :options="roleVocabulary"
                              @update:model-value="updateRoles(contact, $event)"
                            />
                            <button
                              v-else
                              type="button"
                              class="text-xs text-n-blue-text hover:underline"
                              @click="setUpRoles"
                            >
                              {{ t('EMPRESAS.DETAILS.SET_UP_ROLES') }}
                            </button>
                          </td>
                          <!-- Remove -->
                          <td class="text-right">
                            <Button
                              v-tooltip.top="
                                t('EMPRESAS.ACTIONS.REMOVE_CONTACT')
                              "
                              variant="ghost"
                              color="ruby"
                              size="xs"
                              icon="i-lucide-x"
                              class="transition-opacity opacity-0 group-hover:opacity-100"
                              @click="removeContact(contact)"
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Documents tab -->
                <EmpresaDocuments
                  v-if="activeTab === 'documents'"
                  :empresa-id="empresaId"
                />

                <!-- Projects tab -->
                <div
                  v-if="activeTab === 'projects'"
                  class="flex flex-col gap-3"
                >
                  <div class="flex items-center justify-between gap-2">
                    <h3 class="text-sm font-medium text-n-slate-12">
                      {{ t('EMPRESAS.PROJECTS.TITLE') }}
                    </h3>
                    <Button
                      variant="faded"
                      color="slate"
                      size="sm"
                      icon="i-lucide-plus"
                      :label="t('EMPRESAS.PROJECTS.NEW')"
                      @click="projectModalRef?.open()"
                    />
                  </div>

                  <!-- Loading -->
                  <p
                    v-if="isLoadingProjects && !empresaProjects.length"
                    class="py-2 text-sm text-n-slate-10"
                  >
                    {{ t('EMPRESAS.PROJECTS.LOADING') }}
                  </p>

                  <!-- Empty -->
                  <p
                    v-else-if="!empresaProjects.length"
                    class="py-2 text-sm text-n-slate-10"
                  >
                    {{ t('EMPRESAS.PROJECTS.EMPTY') }}
                  </p>

                  <!-- List -->
                  <ul v-else class="flex flex-col divide-y divide-n-weak/60">
                    <li
                      v-for="project in empresaProjects"
                      :key="project.id"
                      class="flex items-center gap-3 py-2.5 transition-colors cursor-pointer group hover:bg-n-alpha-1"
                      role="button"
                      tabindex="0"
                      @click="openProject(project)"
                      @keyup.enter="openProject(project)"
                    >
                      <span
                        class="font-mono text-xs shrink-0 w-16 text-n-slate-10"
                      >
                        PRJ-{{ project.display_id ?? project.id }}
                      </span>
                      <span
                        class="flex-1 min-w-0 text-sm font-medium truncate text-n-slate-12"
                      >
                        {{ project.name }}
                      </span>
                      <span
                        class="inline-flex items-center px-2 py-0.5 text-xs rounded-md shrink-0"
                        :class="projectStatusClass(project.status)"
                      >
                        {{
                          t(
                            `PROJECTS.STATUS.${(
                              project.status || 'draft'
                            ).toUpperCase()}`
                          )
                        }}
                      </span>
                      <span
                        class="i-lucide-chevron-right size-4 shrink-0 text-n-slate-8 transition-colors group-hover:text-n-slate-10"
                      />
                    </li>
                  </ul>
                </div>
              </div>
            </section>
          </div>

          <!-- Sidebar: single box with Atributos / Historial / Notas tabs -->
          <aside class="w-full lg:w-80 shrink-0">
            <section
              class="flex flex-col overflow-hidden border rounded-xl border-n-weak bg-n-solid-2"
            >
              <nav class="flex items-center gap-1 px-2 border-b border-n-weak">
                <button
                  v-for="tab in sidebarTabs"
                  :key="tab.key"
                  type="button"
                  class="px-3 h-10 -mb-px text-sm border-b-2 transition-colors"
                  :class="
                    sidebarTab === tab.key
                      ? 'border-n-brand text-n-slate-12 font-medium'
                      : 'border-transparent text-n-slate-10 hover:text-n-slate-12'
                  "
                  @click="sidebarTab = tab.key"
                >
                  {{ tab.label }}
                </button>
              </nav>

              <div class="p-4">
                <!-- Atributos -->
                <template v-if="sidebarTab === 'atributos'">
                  <p
                    v-if="!empresaCustomFieldDefinitions.length"
                    class="text-sm text-n-slate-10"
                  >
                    {{ t('EMPRESAS.CUSTOM_FIELDS.EMPTY') }}
                  </p>
                  <div v-else class="flex flex-col gap-2">
                    <CustomAttribute
                      v-for="attribute in empresaCustomFieldDefinitions"
                      :key="attribute.id"
                      :label="attribute.attribute_display_name"
                      :description="attribute.attribute_description"
                      :attribute-key="attribute.attribute_key"
                      :attribute-type="attribute.attribute_display_type"
                      :values="attribute.attribute_values"
                      :value="
                        empresaCustomAttributes[attribute.attribute_key] || ''
                      "
                      :attribute-regex="attribute.regex_pattern"
                      :regex-cue="attribute.regex_cue"
                      show-actions
                      @update="onAttributeUpdate"
                    />
                  </div>
                </template>

                <!-- Historial -->
                <EmpresaHistorial
                  v-else-if="sidebarTab === 'historial'"
                  :empresa-id="empresaId"
                />

                <!-- Notas -->
                <EmpresaNotes
                  v-else-if="sidebarTab === 'notas'"
                  :empresa-id="empresaId"
                />
              </div>
            </section>
          </aside>
        </div>
      </div>
    </div>

    <CreateEmpresaModal
      ref="editModalRef"
      :empresa="empresa"
      @updated="loadEmpresa"
    />

    <CreateProjectModal
      ref="projectModalRef"
      :empresa="empresa"
      @created="onProjectCreated"
    />
  </div>
</template>
